import { Button, Group, Modal, TextInput } from "@mantine/core";
import { useForm } from "@mantine/form";
import { notifications } from "@mantine/notifications";
import type { JSX } from "react";
import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useMutation } from "urql";

import { type AiPersonalityCloneMutation, type AiPersonalityCloneMutationVariables, cloneMutation } from "./queries";

interface IProps {
    aiPersonality: {
        id: number;
        name: string;
    };
}

interface FormValues {
    name: string;
}

function CloneButton({ aiPersonality }: IProps): JSX.Element {
    const navigate = useNavigate();
    const [opened, setOpened] = useState(false);
    const [cloneResult, executeClone] = useMutation<AiPersonalityCloneMutation, AiPersonalityCloneMutationVariables>(
        cloneMutation,
    );

    const form = useForm<FormValues>({
        initialValues: {
            name: `${aiPersonality.name} (Clone)`,
        },
        validate: {
            name: (value) => (value.trim().length === 0 ? "Name is required" : null),
        },
    });

    const clone = async (values: FormValues) => {
        const result = await executeClone({
            data: {
                id: aiPersonality.id,
                name: values.name.trim(),
            },
        });

        if (result.error) {
            notifications.show({
                title: "Failed to clone AI personality",
                message: result.error.message,
                color: "red",
            });
        } else if (result.data) {
            const clonedId = result.data.aiPersonalityClone.id;
            notifications.show({
                title: "AI personality cloned",
                message: `Successfully cloned to "${values.name}"`,
            });
            setOpened(false);
            navigate(`/ai-personalities/${clonedId}/edit`);
        }
    };

    return (
        <>
            <Button onClick={() => setOpened(true)}>Clone</Button>

            <Modal opened={opened} onClose={() => setOpened(false)} title="Clone AI Personality" centered>
                <form onSubmit={form.onSubmit(clone)}>
                    <TextInput
                        label="New Name"
                        placeholder="Enter name for the cloned personality"
                        required
                        {...form.getInputProps("name")}
                    />
                    <Group justify="flex-end" mt="md">
                        <Button variant="outline" onClick={() => setOpened(false)}>
                            Cancel
                        </Button>
                        <Button type="submit" loading={cloneResult.fetching}>
                            Clone
                        </Button>
                    </Group>
                </form>
            </Modal>
        </>
    );
}

export default CloneButton;
